<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import {
  XMarkIcon, TrashIcon, TagIcon, UserIcon, IdentificationIcon, EnvelopeIcon, PhoneIcon,
  MapPinIcon, CheckIcon, QrCodeIcon, DocumentTextIcon, ShieldCheckIcon,
  InformationCircleIcon, ExclamationTriangleIcon, DocumentCheckIcon,
  ShoppingCartIcon, UserCircleIcon,
} from '@heroicons/vue/24/outline'
import api from '../services/api'
import { pushEvent } from '../composables/useGtm'
import { useCertificateCart } from '../composables/useCertificateCart'
import { usePhoneMask } from '../composables/usePhoneMask'
import { maskCpfCnpj, normalizeDocument, validateCpf, validateCnpj } from '../utils/document'

const props = defineProps({ initialType: { type: String, default: null } })
const emit = defineEmits(['close'])

const portalUrl = import.meta.env?.VITE_PORTAL_URL || "https://portal.kalisistemas.com.br";

const { items, isEmpty, addItem, removeItem, incQuantity, decQuantity, setAddon, clear } = useCertificateCart()
const cartCount = computed(() => items.value.reduce((sum, i) => sum + i.quantity, 0))

// Anti-spam: honeypot + timestamp de montagem do form.
const hpConfirmEmail = ref('')
const formTs = Math.floor(Date.now() / 1000)

const step = ref('catalog') // catalog | form | address | security | payment
const products = ref([])

// Etapas já preenchidas nesta sessão — o stepper só permite clicar nelas
// (voltar sempre pode; avançar direto só se aquela etapa já tiver sido visitada).
const visitedSteps = reactive(new Set())
watch(step, (s) => visitedSteps.add(s), { immediate: true })

const form = ref({
  holder_name: '', holder_document: '', holder_email: '', holder_phone: '',
  address_cep: '', address_street: '', address_number: '', address_complement: '',
  address_neighborhood: '', address_city: '', address_state: '',
})
const formError = ref('')
const { formatPhone } = usePhoneMask()
function maskHolderDocument(e) {
  form.value.holder_document = maskCpfCnpj(e.target.value)
}
const addressError = ref('')

// Criar senha para acompanhar os pedidos depois (Portal do Cliente).
const wantsAccount = ref(false)
const accountPassword = ref('')

const ufOptions = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO']

// ── Cupom ──
const couponInput = ref('')
const couponApplied = ref(null) // { code, discount, automatic }
const couponError = ref('')
const couponLoading = ref(false)
const couponLocked = ref(false) // já usou cupom antes — não pode digitar um novo

// ── Pagamento ──
const paymentMethod = ref('pix') // pix | boleto
const submitting = ref(false)
const paymentResult = ref(null) // { kind: 'success' | 'followup' | 'error', ... }

function fmtMoney(v) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
}

function productFor(type) {
  return products.value.find(p => p.id === type)
}

function lineUnitPrice(item) {
  const p = productFor(item.certificate_type)
  if (!p) return 0
  const base = p.promo_active ? p.promo_price : p.price
  return base + (item.addon ? (p.addon_price || 0) : 0)
}

function lineSubtotal(item) {
  return lineUnitPrice(item) * item.quantity
}

const subtotal = computed(() => items.value.reduce((sum, i) => sum + lineSubtotal(i), 0))
const discount = computed(() => couponApplied.value?.discount || 0)
const total = computed(() => Math.max(0, subtotal.value - discount.value))
const hasCnpjItem = computed(() => items.value.some(i => i.certificate_type === 'ecnpj-a1'))

// Etapa "Segurança" só aparece se algum item do carrinho tiver addon disponível.
const securityLines = computed(() =>
  items.value
    .map(item => ({ item, product: productFor(item.certificate_type) }))
    .filter(({ product }) => product?.addon_enabled)
)
const hasAnyAddon = computed(() => securityLines.value.length > 0)
const anySelectedAddon = computed(() => securityLines.value.some(({ item }) => item.addon))
const stepFlow = computed(() => hasAnyAddon.value
  ? ['form', 'address', 'security', 'payment']
  : ['form', 'address', 'payment'])
const stepLabels = { form: 'Dados', address: 'Endereço', security: 'Segurança', payment: 'Pagamento' }

async function loadProducts() {
  try {
    const res = await api.get('/public/certificate-products')
    products.value = Array.isArray(res.data) ? res.data : []
  } catch {
    // catálogo indisponível — mantém a lista vazia em vez de quebrar a página
  }
}

function addToCart(productId) {
  addItem(productId)
  goToCart()
}

function goToCart() {
  step.value = stepFlow.value[0]
}

function addMoreItems() {
  step.value = 'catalog'
}

function goNext() {
  const idx = stepFlow.value.indexOf(step.value)
  if (idx >= 0 && idx < stepFlow.value.length - 1) step.value = stepFlow.value[idx + 1]
}

function goToStep(key) {
  // Depois que a compra é concluída (paymentResult preenchido), a etapa 4 já está
  // encerrada — não faz sentido voltar e mexer em dados de um pedido já registrado.
  if (paymentResult.value) return
  if (visitedSteps.has(key)) step.value = key
}

function goBack() {
  if (step.value === 'catalog') { emit('close'); return }
  const idx = stepFlow.value.indexOf(step.value)
  if (idx <= 0) step.value = 'catalog'
  else step.value = stepFlow.value[idx - 1]
}

// Só esvazia o carrinho ao fechar se a compra realmente foi concluída
// (sucesso ou registrada p/ contato da equipe) — em erro ou no meio do fluxo, preserva o carrinho.
function closeModal() {
  if (paymentResult.value && paymentResult.value.kind !== 'error') clear()
  emit('close')
}

// Se o carrinho esvaziar (removeu o último item), volta pro catálogo.
watch(isEmpty, (empty) => { if (empty && step.value !== 'catalog' && !paymentResult.value) step.value = 'catalog' })

// Checa, assim que sabemos CPF/CNPJ + e-mail, se algum desconto automático se aplica
// (cliente Kali Gestão ou renovação/CLIENTEFIEL) e se o cupom manual está bloqueado
// (já usado em compra anterior). Roda sem código nenhum — é só uma consulta.
async function checkAutomaticDiscount() {
  try {
    const res = await api.post('/public/certificate-coupons/validate', {
      holder_document: form.value.holder_document,
      holder_email: form.value.holder_email,
      items: items.value.map(i => ({ certificate_type: i.certificate_type, quantity: i.quantity, addon: i.addon })),
    })
    couponLocked.value = !!res.data?.coupon_locked
    if (res.data?.automatic_discount_label) {
      couponApplied.value = { code: res.data.automatic_discount_label, discount: res.data.discount, automatic: true }
    }
  } catch {
    // desconto automático é best-effort — sem ele, segue sem cupom pré-aplicado
  }
}

async function applyCoupon() {
  const code = couponInput.value.trim()
  if (!code) return
  couponLoading.value = true
  couponError.value = ''
  try {
    const res = await api.post('/public/certificate-coupons/validate', {
      code,
      holder_document: form.value.holder_document,
      holder_email: form.value.holder_email,
      items: items.value.map(i => ({ certificate_type: i.certificate_type, quantity: i.quantity, addon: i.addon })),
    })
    if (res.data?.coupon_valid) {
      couponApplied.value = { code, discount: res.data.discount }
    } else {
      couponApplied.value = null
      couponError.value = res.data?.coupon_message || 'Cupom inválido.'
    }
  } catch {
    couponApplied.value = null
    couponError.value = 'Não foi possível validar o cupom agora.'
  } finally {
    couponLoading.value = false
  }
}

function removeCoupon() {
  if (couponApplied.value?.automatic) return // descontos automáticos não são removíveis
  couponApplied.value = null
  couponInput.value = ''
  couponError.value = ''
}

function validateForm() {
  const f = form.value
  if (!f.holder_name || !f.holder_document || !f.holder_email) {
    formError.value = 'Preencha todos os campos obrigatórios'
    return false
  }
  const rawDoc = normalizeDocument(f.holder_document)
  const docValid = rawDoc.length === 11 ? validateCpf(rawDoc) : rawDoc.length === 14 ? validateCnpj(rawDoc) : false
  if (!docValid) {
    formError.value = 'CPF/CNPJ inválido'
    return false
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.holder_email)) {
    formError.value = 'E-mail inválido'
    return false
  }
  if (wantsAccount.value && accountPassword.value.length < 8) {
    formError.value = 'A senha precisa ter pelo menos 8 caracteres'
    return false
  }
  formError.value = ''
  return true
}

function continueFromForm() {
  if (!validateForm()) return
  checkAutomaticDiscount()
  goNext()
}

function validateAddress() {
  const f = form.value
  if (!f.address_cep || !f.address_street || !f.address_number || !f.address_neighborhood || !f.address_city || !f.address_state) {
    addressError.value = 'Preencha todos os campos obrigatórios'
    return false
  }
  addressError.value = ''
  return true
}

function continueFromAddress() {
  if (!validateAddress()) return
  goNext()
}

async function submit() {
  submitting.value = true
  paymentResult.value = null
  try {
    const res = await api.post('/public/certificate-orders', {
      items: items.value.map(i => ({ certificate_type: i.certificate_type, quantity: i.quantity, addon: i.addon })),
      // Descontos automáticos (Kali Gestão/CLIENTEFIEL) são recalculados no backend a partir do
      // CPF/CNPJ + e-mail — só envia coupon_code quando for um cupom digitado manualmente.
      coupon_code: couponApplied.value && !couponApplied.value.automatic ? couponApplied.value.code : null,
      confirm_email: hpConfirmEmail.value,
      form_ts: formTs,
      ...form.value,
      // Campo único (CPF/CNPJ + Nome/Razão Social) serve tanto pessoa física quanto jurídica —
      // quando o carrinho tem e-CNPJ, os mesmos valores também preenchem os campos de empresa.
      company_name: hasCnpjItem.value ? form.value.holder_name : null,
      company_cnpj: hasCnpjItem.value ? form.value.holder_document : null,
      create_account: wantsAccount.value,
      password: wantsAccount.value ? accountPassword.value : null,
    })
    const accessToken = res.data?.data?.access_token
    if (!accessToken) {
      // SpamGuard devolveu sucesso genérico — não segue o fluxo.
      paymentResult.value = { kind: 'error', message: 'Não foi possível registrar seu pedido. Tente novamente.' }
      return
    }

    const d = res.data.data
    pushEvent('certificate_order', { items: items.value.length, final_amount: d.final_amount })
    // O carrinho só é limpo quando o modal fecha de fato (closeModal) — assim o Resumo
    // continua mostrando o que foi comprado enquanto o resultado é exibido nesta mesma tela.

    try {
      const payRes = await api.post(`/public/certificate-orders/${accessToken}/pay`)
      const pd = payRes.data.data
      paymentResult.value = {
        kind: 'success',
        orderCode: d.order_code,
        boletoUrl: pd.boleto_url,
        pixCode: pd.boleto_pix_qrcode,
      }
    } catch {
      // Esperado hoje: documentos ainda não enviados, pagamento é liberado após confirmação da equipe.
      paymentResult.value = {
        kind: 'followup',
        orderCode: d.order_code,
        email: d.holder_email,
      }
    }
  } catch (e) {
    paymentResult.value = { kind: 'error', message: e?.response?.data?.message || 'Não foi possível registrar seu pedido. Tente novamente.' }
  } finally {
    submitting.value = false
  }
}

async function fetchCep() {
  const cep = form.value.address_cep?.replace(/\D/g, '')
  if (!cep || cep.length !== 8) return
  try {
    const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
    const d = await r.json()
    if (!d.erro) {
      form.value.address_street = d.logradouro || ''
      form.value.address_neighborhood = d.bairro || ''
      form.value.address_city = d.localidade || ''
      form.value.address_state = d.uf || ''
    }
  } catch {
    // busca de CEP indisponível — usuário preenche o endereço manualmente
  }
}

onMounted(() => {
  loadProducts()
  if (props.initialType) {
    addItem(props.initialType)
    step.value = 'form'
  } else if (!isEmpty.value) {
    // Já tem item no carrinho (ex.: abriu pelo botão "Carrinho") — pula o catálogo e vai direto pro checkout.
    step.value = 'form'
  }
  // Carrinho vazio e sem tipo específico: mantém o padrão 'catalog' pra escolher um certificado.
})
</script>

<template>
  <div class="fixed inset-0 z-[9999] bg-white flex flex-col">

    <!-- Header -->
    <div class="shrink-0 bg-white border-b border-gray-100">
      <div class="px-6 sm:px-8 py-4 flex items-center justify-between">
        <div class="flex items-center gap-4 min-w-0">
          <img src="@/assets/logo.png" alt="Kali Sistemas" class="h-7 w-auto shrink-0" />
          <span class="hidden sm:block w-px h-6 bg-gray-200 shrink-0"></span>
          <div class="flex items-center gap-2 min-w-0">
            <button v-if="step !== 'catalog' && !paymentResult" @click="goBack" class="text-gray-400 hover:text-gray-600 text-lg shrink-0">←</button>
            <h2 class="text-base sm:text-lg font-bold text-gray-900 truncate">
              {{ step === 'catalog' ? 'Certificado Digital' : stepLabels[step] }}
            </h2>
          </div>
        </div>
        <button @click="closeModal" class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-full transition shrink-0">
          <XMarkIcon class="w-6 h-6" />
        </button>
      </div>

      <!-- Carrinho + Meus pedidos -->
      <div class="px-6 sm:px-8 pb-3 flex items-center justify-end gap-3 text-sm">
        <a :href="portalUrl" target="_blank" rel="noopener"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50 font-semibold text-gray-700 transition hover:border-gray-300 hover:bg-gray-100 hover:text-[#004a60]">
          <UserCircleIcon class="w-4 h-4" />
          Meus pedidos
        </a>
        <button
          @click="!paymentResult && goToCart()"
          :disabled="!!paymentResult"
          class="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50 font-semibold text-gray-700 transition hover:border-gray-300 hover:bg-gray-100 hover:text-[#004a60] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ShoppingCartIcon class="w-4 h-4" />
          Carrinho
          <span v-if="!isEmpty"
            class="absolute -top-1.5 -right-2.5 bg-[#E5BE35] text-slate-900 text-[10px] font-bold rounded-full h-[16px] min-w-[16px] flex items-center justify-center px-1">
            {{ cartCount }}
          </span>
        </button>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      <!-- CATÁLOGO -->
      <div v-if="step === 'catalog'" class="relative min-h-full flex items-center justify-center py-6 px-6 overflow-hidden">
        <!-- Atmosfera de fundo -->
        <div class="absolute -top-20 -left-20 w-80 h-80 bg-[#004a60]/[0.06] rounded-full blur-3xl pointer-events-none"></div>
        <div class="absolute -bottom-24 -right-24 w-80 h-80 bg-[#E5BE35]/[0.12] rounded-full blur-3xl pointer-events-none"></div>

        <div class="relative w-full max-w-4xl">
          <div class="text-center mb-5">
            <span class="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider uppercase text-[#004a60] bg-[#004a60]/5 rounded-full px-3 py-1 border border-[#004a60]/10">
              <ShieldCheckIcon class="w-3.5 h-3.5" /> ICP-Brasil
            </span>
            <h1 class="mt-3 text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
              Escolha seu certificado <span class="text-[#E5BE35]">digital</span>
            </h1>
            <p class="mt-1.5 text-sm text-gray-500 max-w-md mx-auto">Emissão 100% online, validade jurídica garantida e suporte na instalação.</p>
          </div>

          <div class="grid sm:grid-cols-2 gap-5">
            <div v-for="p in products.filter(p => p.enabled)" :key="p.id"
              class="flex flex-col bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all hover:shadow-xl hover:-translate-y-1">

              <!-- Header sólido -->
              <div :class="['flex items-center justify-between px-5 py-3', p.id === 'ecnpj-a1' ? 'bg-[#004a60]' : 'bg-[#E5BE35]']">
                <span :class="['font-extrabold text-lg', p.id === 'ecnpj-a1' ? 'text-white' : 'text-slate-900']">{{ p.name }}</span>
                <span :class="['text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full', p.id === 'ecnpj-a1' ? 'bg-white/15 text-white' : 'bg-slate-900/10 text-slate-900']">
                  {{ p.id === 'ecnpj-a1' ? 'Pessoa Jurídica' : 'Pessoa Física' }}
                </span>
              </div>

              <div class="flex flex-col flex-1 p-5">
                <p class="text-sm text-gray-500">
                  {{ p.id === 'ecnpj-a1' ? 'Para empresas que emitem NF-e e precisam acessar portais governamentais.' : 'Para sócios, contadores e profissionais liberais.' }}
                </p>

                <div class="mt-3 flex items-baseline gap-2">
                  <span v-if="p.promo_active" class="text-sm text-gray-400 line-through">{{ fmtMoney(p.price) }}</span>
                  <span class="text-2xl font-extrabold text-[#004a60]">{{ fmtMoney(p.promo_active ? p.promo_price : p.price) }}</span>
                </div>
                <p v-if="p.promo_active && p.promo_end_date" class="text-xs font-medium text-orange-600 mt-0.5">
                  Promoção válida até {{ new Date(p.promo_end_date + 'T00:00:00').toLocaleDateString('pt-BR') }}
                </p>

                <ul class="mt-3 text-sm text-gray-600 space-y-1 flex-1">
                  <li v-for="feat in (p.id === 'ecnpj-a1'
                    ? ['Emissão de NF-e, NFS-e e CT-e', 'Acesso ao e-CAC e Receita Federal', 'Envio de declarações e obrigações fiscais', 'Assinatura de documentos digitais', 'e-Social e Conectividade Social', 'Validade de 12 meses']
                    : ['Assinatura digital de contratos e documentos', 'Acesso à Receita Federal e e-CAC', 'Envio de declaração de Imposto de Renda', 'Uso em e-Social e Conectividade Social', 'Acesso a bancos públicos e privados', 'Validade de 12 meses'])" :key="feat" class="flex items-start gap-2">
                    <span class="text-emerald-500 font-bold shrink-0">✓</span> {{ feat }}
                  </li>
                </ul>

                <div class="mt-4 grid grid-cols-2 gap-3">
                  <div class="border border-gray-200 rounded-lg px-3 py-1.5 text-center">
                    <div class="text-xs text-gray-500">Armazenamento</div>
                    <div class="text-sm font-semibold text-gray-800">Arquivo (A1)</div>
                  </div>
                  <div class="border border-gray-200 rounded-lg px-3 py-1.5 text-center">
                    <div class="text-xs text-gray-500">Mídia</div>
                    <div class="text-sm font-semibold text-gray-800">Sem token</div>
                  </div>
                </div>

                <button @click="addToCart(p.id)"
                  :class="['mt-4 w-full text-sm py-2.5 rounded-lg font-semibold transition shadow-sm hover:shadow-md', p.id === 'ecnpj-a1' ? 'bg-[#004a60] text-white hover:bg-[#003e4d]' : 'bg-[#E5BE35] text-slate-900 hover:bg-[#d1ac2c]']">
                  Comprar {{ p.name }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- FORM / ENDEREÇO / SEGURANÇA / PAGAMENTO — layout 2 colunas -->
      <div v-else class="max-w-5xl mx-auto grid md:grid-cols-[minmax(0,360px)_1px_1fr] gap-10 p-8 md:p-10">

        <!-- Coluna esquerda: Resumo -->
        <div>
          <h3 class="text-base font-bold text-gray-900 mb-4">Resumo</h3>
          <div class="space-y-3">
            <div v-for="item in items" :key="item.certificate_type" class="border border-gray-200 rounded-xl p-4 flex gap-3">
              <div class="w-16 h-16 rounded-lg bg-[#004a60]/10 flex items-center justify-center shrink-0">
                <DocumentCheckIcon class="w-8 h-8 text-[#004a60]" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="font-bold text-sm text-gray-900">{{ productFor(item.certificate_type)?.name }}</div>
                <div class="text-xs text-gray-500 mt-0.5">
                  Validade 12 meses
                  <span v-if="item.addon" class="text-emerald-600 font-medium"> · {{ productFor(item.certificate_type)?.addon_name }}</span>
                </div>
                <div class="flex items-center gap-2 mt-2">
                  <button @click="decQuantity(item.certificate_type)" class="w-6 h-6 border border-gray-300 rounded text-gray-600 hover:bg-gray-50">−</button>
                  <span class="text-sm w-4 text-center">{{ item.quantity }}</span>
                  <button @click="incQuantity(item.certificate_type)" class="w-6 h-6 border border-gray-300 rounded text-gray-600 hover:bg-gray-50">+</button>
                  <span class="ml-auto font-bold text-[#004a60]">{{ fmtMoney(lineSubtotal(item)) }}</span>
                </div>
              </div>
              <button @click="removeItem(item.certificate_type)" class="text-gray-300 hover:text-red-500 shrink-0"><TrashIcon class="w-5 h-5" /></button>
            </div>
          </div>

          <button v-if="!paymentResult" @click="addMoreItems"
            class="mt-3 w-full flex items-center justify-center gap-1.5 border border-dashed border-gray-300 text-gray-500 text-sm font-medium py-2.5 rounded-lg hover:border-[#004a60] hover:text-[#004a60] transition">
            <span class="text-base leading-none">+</span> Adicionar mais itens
          </button>

          <!-- Cupom -->
          <div class="mt-5">
            <label class="text-xs font-semibold text-gray-500" for="coupon_input">Cupom de desconto</label>

            <!-- Desconto automático (Kali Gestão / CLIENTEFIEL) — não é removível nem digitável -->
            <div v-if="couponApplied?.automatic" class="mt-1 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5">
              <ShieldCheckIcon class="w-4 h-4 text-emerald-600 shrink-0" />
              <span class="text-sm text-emerald-700 font-medium">Desconto "{{ couponApplied.code }}" aplicado automaticamente</span>
            </div>

            <template v-else>
              <div class="flex gap-2 mt-1">
                <div class="relative flex-1">
                  <TagIcon class="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input id="coupon_input" v-model="couponInput" :disabled="!!couponApplied || couponLocked" class="input pl-9" placeholder="INSIRA SEU CUPOM" @keyup.enter="applyCoupon" />
                </div>
                <button v-if="!couponApplied" @click="applyCoupon" :disabled="couponLoading || couponLocked"
                  class="px-4 rounded-lg border-2 border-[#004a60] text-[#004a60] font-semibold text-sm hover:bg-[#004a60]/5 disabled:opacity-50">
                  {{ couponLoading ? '...' : 'Aplicar' }}
                </button>
                <button v-else @click="removeCoupon" class="px-4 rounded-lg border-2 border-gray-300 text-gray-500 font-semibold text-sm hover:bg-gray-50">
                  Remover
                </button>
              </div>
              <p v-if="couponLocked" class="text-xs text-gray-400 mt-1">Você já utilizou um cupom em uma compra anterior.</p>
              <p v-if="couponError" class="text-xs text-red-500 mt-1">{{ couponError }}</p>
              <p v-if="couponApplied" class="text-xs text-emerald-600 mt-1">Cupom {{ couponApplied.code }} aplicado</p>
            </template>
          </div>

          <!-- Totais -->
          <div class="mt-5 pt-4 border-t border-gray-100 space-y-1.5 text-sm">
            <div class="flex justify-between text-gray-500"><span>Subtotal</span><span>{{ fmtMoney(subtotal) }}</span></div>
            <div class="flex justify-between text-gray-500"><span>Desconto</span><span>{{ fmtMoney(discount) }}</span></div>
            <div class="flex justify-between font-bold text-base text-gray-900 pt-2 border-t border-gray-100 mt-2">
              <span>Valor Total</span><span>{{ fmtMoney(total) }}</span>
            </div>
          </div>
        </div>

        <div class="hidden md:block bg-gray-100"></div>

        <!-- Coluna direita: Stepper + etapas -->
        <div>
          <!-- Stepper -->
          <div class="flex items-center gap-3 mb-10 w-full max-w-md">
            <template v-for="(key, idx) in stepFlow" :key="key">
              <button
                type="button"
                @click="goToStep(key)"
                :disabled="!visitedSteps.has(key) || !!paymentResult"
                :class="[
                  'w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition',
                  stepFlow.indexOf(step) > idx
                    ? 'bg-[#004a60] text-white ring-2 ring-[#E5BE35] ring-offset-2'
                    : stepFlow.indexOf(step) === idx ? 'bg-[#004a60] text-white' : 'bg-gray-100 text-gray-400 border border-gray-300',
                  visitedSteps.has(key) && !paymentResult ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed',
                ]"
              >
                <CheckIcon v-if="stepFlow.indexOf(step) > idx" class="w-4 h-4" />
                <span v-else>{{ idx + 1 }}</span>
              </button>
              <div v-if="idx < stepFlow.length - 1" :class="['flex-1 h-0.5', stepFlow.indexOf(step) > idx ? 'bg-[#004a60]' : 'bg-gray-200']"></div>
            </template>
          </div>

          <!-- STEP: Dados -->
          <template v-if="step === 'form'">
            <h3 class="text-2xl font-bold text-[#004a60] mb-5">Insira seus dados para continuar a compra</h3>

            <div class="flex items-start gap-3 bg-sky-50 border border-sky-100 rounded-xl p-4 mb-5">
              <InformationCircleIcon class="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
              <div class="text-xs text-sky-900">
                <b class="block text-sm mb-0.5">Aviso importante</b>
                A nota fiscal será emitida com as informações fornecidas no checkout. Por favor, verifique essas informações antes de finalizar seu pedido.
              </div>
            </div>

            <div v-if="formError" class="bg-red-50 text-red-600 text-sm font-medium rounded-lg px-4 py-3 mb-4">
              {{ formError }}
            </div>

            <div aria-hidden="true" style="position: absolute; left: -9999px; top: -9999px; width: 0; height: 0; overflow: hidden">
              <input v-model="hpConfirmEmail" type="text" name="confirm_email" tabindex="-1" autocomplete="off" />
            </div>

            <div class="space-y-4">
              <div>
                <label class="label" for="holder_document">{{ hasCnpjItem ? 'CPF/CNPJ *' : 'CPF *' }}</label>
                <div class="relative">
                  <IdentificationIcon class="icon" />
                  <input id="holder_document" v-model="form.holder_document" class="input pl-9" placeholder="000.000.000-00" @input="maskHolderDocument" />
                </div>
              </div>
              <div>
                <label class="label" for="holder_name">Nome / Razão Social *</label>
                <div class="relative">
                  <UserIcon class="icon" />
                  <input id="holder_name" v-model="form.holder_name" class="input pl-9" placeholder="Seu nome ou razão social" />
                </div>
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="label" for="holder_email">E-mail *</label>
                  <div class="relative">
                    <EnvelopeIcon class="icon" />
                    <input id="holder_email" v-model="form.holder_email" type="email" class="input pl-9" placeholder="Seu e-mail" />
                  </div>
                </div>
                <div>
                  <label class="label" for="holder_phone">Celular *</label>
                  <div class="relative">
                    <PhoneIcon class="icon" />
                    <input id="holder_phone" v-model="form.holder_phone" class="input pl-9" placeholder="Seu telefone" @input="form.holder_phone = formatPhone($event.target.value)" />
                  </div>
                </div>
              </div>
            </div>

            <label class="flex items-center gap-2 mt-5 cursor-pointer">
              <input type="checkbox" v-model="wantsAccount" class="text-kali-blue-500 focus:ring-kali-blue-500 w-4 h-4" />
              <span class="text-sm text-gray-600">Deseja colocar senha para o próximo acesso/compra?</span>
            </label>
            <div v-if="wantsAccount" class="mt-3">
              <label class="label" for="accountPassword">Crie uma senha *</label>
              <input id="accountPassword" v-model="accountPassword" type="password" class="input pl-3" placeholder="Mínimo 8 caracteres" />
              <p class="text-xs text-gray-400 mt-1">Com ela você acompanha seus certificados no Portal do Cliente.</p>
            </div>

            <button @click="continueFromForm" class="w-full mt-6 bg-[#004a60] text-white py-3 rounded-lg font-semibold hover:bg-[#003e4d] transition">
              Continuar →
            </button>
          </template>

          <!-- STEP: Endereço -->
          <template v-else-if="step === 'address'">
            <h3 class="text-2xl font-bold text-[#004a60] mb-5">Endereço</h3>

            <div class="flex items-start gap-3 bg-sky-50 border border-sky-100 rounded-xl p-4 mb-5">
              <InformationCircleIcon class="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
              <div class="text-xs text-sky-900">
                <b class="block text-sm mb-0.5">Aviso importante</b>
                A nota fiscal será emitida com as informações fornecidas no checkout. Por favor, verifique essas informações antes de finalizar seu pedido.
              </div>
            </div>

            <div v-if="addressError" class="bg-red-50 text-red-600 text-sm font-medium rounded-lg px-4 py-3 mb-4">
              {{ addressError }}
            </div>

            <div class="space-y-4">
              <div>
                <label class="label" for="address_cep">CEP *</label>
                <div class="relative">
                  <MapPinIcon class="icon" />
                  <input id="address_cep" v-model="form.address_cep" class="input pl-9" placeholder="00000-000" @blur="fetchCep" />
                </div>
              </div>
              <div><label class="label" for="address_street">Endereço *</label><input id="address_street" v-model="form.address_street" class="input pl-3" /></div>
              <div class="grid grid-cols-2 gap-3">
                <div><label class="label" for="address_neighborhood">Bairro *</label><input id="address_neighborhood" v-model="form.address_neighborhood" class="input pl-3" /></div>
                <div><label class="label" for="address_number">Número *</label><input id="address_number" v-model="form.address_number" class="input pl-3" /></div>
              </div>
              <div><label class="label" for="address_complement">Complemento</label><input id="address_complement" v-model="form.address_complement" class="input pl-3" placeholder="Opcional" /></div>
              <div class="grid grid-cols-2 gap-3">
                <div><label class="label" for="address_state">UF *</label><select id="address_state" v-model="form.address_state" class="input pl-3"><option value="">UF</option><option v-for="u in ufOptions" :key="u">{{ u }}</option></select></div>
                <div><label class="label" for="address_city">Cidade *</label><input id="address_city" v-model="form.address_city" class="input pl-3" /></div>
              </div>
            </div>

            <button @click="continueFromAddress" class="w-full mt-6 bg-[#004a60] text-white py-3 rounded-lg font-semibold hover:bg-[#003e4d] transition">
              Continuar →
            </button>
          </template>

          <!-- STEP: Segurança (addon opcional) -->
          <template v-else-if="step === 'security'">
            <h3 class="text-2xl font-bold text-[#004a60] mb-5">Segurança</h3>

            <div class="border border-gray-200 rounded-xl p-5 mb-6">
              <div class="flex items-center gap-2 mb-3">
                <ShieldCheckIcon class="w-5 h-5 text-[#004a60]" />
                <span class="font-bold text-sm text-gray-900">{{ securityLines[0]?.product?.addon_name || 'Proteção' }}</span>
              </div>
              <p class="text-sm text-gray-500 mb-4">
                Reemissão simplificada do seu certificado digital em caso de perda ou atualização.
                Se você perder ou esquecer a senha, a reemissão é gratuita.
              </p>
              <p class="text-xs font-semibold text-gray-500 uppercase mb-2">Escolha para qual certificado você quer a proteção</p>
              <label v-for="{ item, product } in securityLines" :key="item.certificate_type"
                class="flex items-center justify-between gap-3 py-2 cursor-pointer">
                <span class="flex items-center gap-2 text-sm text-gray-800">
                  <input type="checkbox" :checked="item.addon" @change="setAddon(item.certificate_type, $event.target.checked)" class="text-kali-blue-500 focus:ring-kali-blue-500 w-4 h-4" />
                  {{ product.name }}
                </span>
                <span class="text-sm font-semibold text-gray-700">{{ fmtMoney(product.addon_price) }}</span>
              </label>
            </div>

            <button @click="goNext" class="w-full bg-[#004a60] text-white py-3 rounded-lg font-semibold hover:bg-[#003e4d] transition">
              {{ anySelectedAddon ? 'Confirmar proteção e continuar' : 'Continuar sem proteção' }}
            </button>
          </template>

          <!-- STEP: Pagamento -->
          <template v-else-if="step === 'payment'">
            <template v-if="!paymentResult">
              <h3 class="text-2xl font-bold text-[#004a60] mb-5">Escolha a forma de pagamento</h3>

              <div class="space-y-3.5">
                <label :class="['flex items-center gap-3 border-2 rounded-xl p-4 cursor-pointer transition', paymentMethod === 'pix' ? 'border-[#004a60]' : 'border-gray-200 hover:border-gray-300']">
                  <input type="radio" value="pix" v-model="paymentMethod" class="text-kali-blue-500 focus:ring-kali-blue-500 w-4 h-4" />
                  <QrCodeIcon class="w-5 h-5 text-[#004a60]" />
                  <span class="font-semibold text-sm text-gray-800">Pagamento com Pix</span>
                </label>
                <div v-if="paymentMethod === 'pix'" class="text-xs text-gray-500 pl-4 -mt-2 space-y-2">
                  <p>Pague com Pix em qualquer dia e a qualquer hora! O pagamento é instantâneo, prático e pode ser feito em poucos segundos. É muito rápido e seguro.</p>
                  <ol class="space-y-1 list-decimal list-inside">
                    <li>Ao clicar no botão "Finalizar compra" nesta página, será gerado um QR Code e um Pix Copia e Cola</li>
                    <li>Abra o app do seu banco ou instituição financeira e entre no ambiente Pix</li>
                    <li>Caso esteja fazendo a compra pelo computador, escolha a opção Pagar com QR Code</li>
                    <li>Caso esteja fazendo a compra pelo celular, escolha a opção Pagar com Pix Copia e Cola</li>
                    <li>Confira as informações no app do banco e conclua a compra</li>
                  </ol>
                  <p>*Após o procedimento, em alguns segundos esta página será atualizada automaticamente reconhecendo o pagamento.</p>
                </div>

                <label :class="['flex items-center gap-3 border-2 rounded-xl p-4 cursor-pointer transition', paymentMethod === 'boleto' ? 'border-[#004a60]' : 'border-gray-200 hover:border-gray-300']">
                  <input type="radio" value="boleto" v-model="paymentMethod" class="text-kali-blue-500 focus:ring-kali-blue-500 w-4 h-4" />
                  <DocumentTextIcon class="w-5 h-5 text-[#004a60]" />
                  <span class="font-semibold text-sm text-gray-800">Pagamento com Boleto</span>
                </label>
                <div v-if="paymentMethod === 'boleto'" class="text-xs text-gray-500 pl-4 -mt-2 space-y-1">
                  <p>*Para que haja a liberação do pedido, após pagar o boleto o prazo bancário para a sua compensação é de até 3 dias úteis.</p>
                  <p>*Com o Pix o pagamento é instantâneo, prático e pode ser feito em poucos segundos.</p>
                </div>
              </div>

              <button @click="submit" :disabled="submitting"
                class="w-full mt-6 bg-[#004a60] text-white py-3 rounded-lg font-semibold hover:bg-[#003e4d] transition disabled:opacity-60 flex items-center justify-center gap-2">
                <svg v-if="submitting" class="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                {{ submitting ? 'Processando pagamento, aguarde...' : 'Finalizar compra' }}
              </button>
            </template>

            <!-- Resultado: sucesso com boleto/pix -->
            <div v-else-if="paymentResult.kind === 'success'" class="text-center py-6">
              <div class="relative w-16 h-16 mx-auto mb-4">
                <div class="absolute inset-0 rounded-full border-[3px] border-[#E5BE35]"></div>
                <div class="absolute inset-[5px] rounded-full bg-[#004a60] flex items-center justify-center">
                  <CheckIcon class="w-7 h-7 text-white" />
                </div>
              </div>
              <h3 class="text-lg font-bold text-gray-900">Pedido realizado!</h3>
              <p class="text-sm text-gray-500 mt-1">Código do pedido: <b class="font-mono tracking-wide text-gray-700">{{ paymentResult.orderCode }}</b></p>
              <a v-if="paymentResult.boletoUrl" :href="paymentResult.boletoUrl" target="_blank" rel="noopener noreferrer"
                class="inline-block mt-4 bg-[#004a60] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#003e4d] transition">
                Abrir boleto / PIX ↗
              </a>
              <button @click="closeModal" class="mt-6 border-2 border-gray-200 text-gray-600 px-6 py-2.5 rounded-lg font-semibold hover:bg-gray-50 transition">Fechar</button>
            </div>

            <!-- Resultado: pedido registrado, pagamento a confirmar pela equipe -->
            <div v-else-if="paymentResult.kind === 'followup'" class="text-center py-6">
              <div class="relative w-16 h-16 mx-auto mb-4">
                <div class="absolute inset-0 rounded-full border-[3px] border-[#E5BE35]"></div>
                <div class="absolute inset-[5px] rounded-full bg-[#004a60] flex items-center justify-center">
                  <CheckIcon class="w-7 h-7 text-white" />
                </div>
              </div>
              <h3 class="text-lg font-bold text-gray-900">Pedido registrado com sucesso!</h3>
              <p class="text-sm text-gray-500 mt-1">Código do pedido: <b class="font-mono tracking-wide text-gray-700">{{ paymentResult.orderCode }}</b></p>
              <p class="text-sm text-gray-600 mt-3 max-w-sm mx-auto">
                Nossa equipe vai confirmar seus dados e enviar o link de pagamento para <b>{{ paymentResult.email }}</b> em breve.
              </p>
              <button @click="closeModal" class="mt-6 bg-[#004a60] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#003e4d] transition">Fechar</button>
            </div>

            <!-- Resultado: erro real -->
            <div v-else-if="paymentResult.kind === 'error'" class="text-center py-6">
              <ExclamationTriangleIcon class="w-14 h-14 text-red-400 mx-auto mb-3" />
              <h3 class="text-lg font-bold text-gray-900">Não foi possível concluir</h3>
              <p class="text-sm text-gray-500 mt-2 max-w-sm mx-auto">{{ paymentResult.message }}</p>
              <button @click="paymentResult = null" class="mt-6 bg-[#004a60] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#003e4d] transition">Tentar novamente</button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Rodapé de confiança -->
    <div class="shrink-0 border-t border-gray-100 px-6 py-3 flex items-center justify-center gap-6 flex-wrap text-gray-400">
      <div class="flex items-center gap-1.5 text-xs font-medium">
        <ShieldCheckIcon class="w-4 h-4" /> ICP-Brasil
      </div>
      <div class="flex items-center gap-1.5 text-xs font-medium">
        <DocumentTextIcon class="w-4 h-4" /> Boleto
      </div>
      <div class="flex items-center gap-1.5 text-xs font-medium">
        <QrCodeIcon class="w-4 h-4" /> Pix
      </div>
    </div>
  </div>
</template>

<style scoped>
.label { display: block; font-size: 12px; font-weight: 600; color: #6b7280; margin-bottom: 4px; }
.input { width: 100%; border: 1px solid #d1d5db; border-radius: 8px; padding-top: 9px; padding-bottom: 9px; padding-right: 12px; font-size: 14px; outline: none; transition: border-color .15s; }
.input:focus { border-color: #004a60; box-shadow: 0 0 0 2px rgba(0,77,107,.1); }
.icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); width: 16px; height: 16px; color: #9ca3af; pointer-events: none; }
</style>
